import React, { useEffect, useMemo, useRef, useState } from 'react';
import { active_project, fullViewMode, vis_list }         from 'state/store.global';
import { vis_filter, vis_detail } from 'state/store.global';
import { vis_desktop_nav }        from 'state/store.global';
import { getFocus, useAtom, useGet }                 from 'state/jotai';
import { Content, Root }          from './Layout.style';
import { DualMenu }               from 'components/DualMenu/DualMenu';
import { TopMenu }                from 'components/TopMenu/TopMenu';
import { useLocation }            from 'react-router-dom';
import { useBreak }               from 'hooks/useBreak';
import useUnitPage from 'components/Unit/useUnitPage';
import { Scrollbar } from 'react-scrollbars-custom';
import { _bootstrap } from 'state/store.bootstrap';

/**
 * Throttle helper (simple)
 * Calls fn at most once every wait ms. Returns a throttled function.
 */
function throttle(fn, wait = 150) {
  let last = 0;
  let timeout = null;
  return (...args) => {
    const now = Date.now();
    const remaining = wait - (now - last);
    if (remaining <= 0) {
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
      last = now;
      fn(...args);
    } else if (!timeout) {
      timeout = setTimeout(() => {
        last = Date.now();
        timeout = null;
        fn(...args);
      }, remaining);
    }
  };
}

export const Layout = ({ children }) => {
  const [viewportHeight, setViewportHeight] = useState(0);
  const here = useLocation().pathname;
  const isDesktop = useBreak('md_up');

  // children destructure (as before)
  const { Navigation, DrawersLeft, DrawersRight, Views } = children;

  // Jotai reads (kept as-is for safety)
  const [viewMode, setViewMode] = useAtom(fullViewMode);
  const pin = useGet(active_project).pin;
  const left = useGet(vis_filter);
  const right = useGet(vis_detail);
  const nav = useGet(vis_desktop_nav);
  const list = useGet(vis_list);
  const projectType = getFocus(_bootstrap, "type");

  // setter for vis_list
  const [, setShowList] = useAtom(vis_list);

  // route mapping
  const activeURL = ["list", "compare", "unit", "isometry", "gallery", "downloads", "favorites"];
  const activeURLindex = Math.max(0, activeURL.findIndex(i => here.includes(i)));

  // ===========================================================================
  // Memoize style objects so the same reference is passed to styled components
  // ===========================================================================
  const stylesMemo = useMemo(() => {
    // Use viewportHeight computed value; the consumer expects an object in the same shape
    const vh = viewportHeight || window?.innerHeight || 0;

    return {
      isometry: {
      marginTop: isDesktop ? "72px": pin === 0 ? "72px": "143px",
        height: isDesktop
          ? pin > 0
            ? `calc(${vh}px - 166px)`
            : `calc(${vh}px - 72px)`
          : `calc(${vh}px - 154px)`
      },
      list: {
        marginTop: isDesktop ? "72px" : "142px",
        height: isDesktop ? "calc(100% - 158px)" : right ? "500px" : "calc(100% - 172px)",
        paddingBottom: isDesktop ? '' : "77px",
        overflow: right ? 'hidden' : 'auto',
      },
      unit: {
        marginTop: isDesktop ? "72px" : "69px",
        height: isDesktop ? "calc(100% - 72px)" : "calc(100% - 69px)",
      },
      compare: {
        marginTop: isDesktop ? "200px" : "170px",
        height: isDesktop ? "calc(100% - 200px)" : "calc(100% - 119px)",
        overflow: "hidden"
      },
      favorites: {
        marginTop: isDesktop ? "134px" : "119px",
        height: isDesktop ? "calc(100% - 119px)" : "calc(100% - 119px)",
      },
      gallery: {
        marginTop: isDesktop ? "125px" : "125px",
        height: isDesktop ? "calc(100% - 125px)" : "calc(100% - 125px)"
      },
      downloads: {
        marginTop: isDesktop ? "135px" : "119px",
        height: isDesktop ? "calc(100% - 135px)" : "calc(100% - 119px)"
      }
    };
  }, [isDesktop, pin, right, viewportHeight]);

  const rootStyleMemo = useMemo(() => ({
    isometry: {
      height: "100%",
      overflowY: "hidden"
    }
  }), []);

  // ===========================================================================
  // Memoize Drawers so React doesn't recreate/rehydrate them every render
  // ===========================================================================
  const memoizedLeftDrawers = useMemo(() => {
    // keep original array items as-is, but stabilize the reference
    return DrawersLeft && DrawersLeft.length ? DrawersLeft : [];
  }, [DrawersLeft]);

  const memoizedRightDrawers = useMemo(() => {
    return DrawersRight && DrawersRight.length ? DrawersRight : [];
  }, [DrawersRight]);

  // ===========================================================================
  // Guarded effect to set list when condition changes to true.
  // Avoid repeatedly setting it if list already true.
  // Included all dependencies used inside effect.
  // ===========================================================================

  // ===========================================================================
  // Throttled resize watcher to reduce re-render frequency
  // ===========================================================================
  useEffect(() => {
    const updateViewportHeight = () => {
      // small guard to avoid setting same value repeatedly
      const h = window.innerHeight;
      setViewportHeight(prev => (prev === h ? prev : h));
    };

    // throttle to ~120-200ms (safe default)
    const throttled = throttle(updateViewportHeight, 150);

    // initial
    throttled();

    window.addEventListener('resize', throttled, { passive: true });
    return () => {
      window.removeEventListener('resize', throttled);
    };
  }, []);

  // pick current styles safely
  const currentPath = activeURL[activeURLindex] || 'list';
  const currentStyles = stylesMemo[currentPath] || {};
  const currentRootStyles = rootStyleMemo[currentPath] || {};

  return (
    <Root styles={currentRootStyles}>
      { pin >= 0 && memoizedLeftDrawers.map(i => i) }
      { pin >= 0 && memoizedRightDrawers.map(i => i) }

      <Content
        path={currentPath}
        left={left}
        viewMode={viewMode}
        right={viewMode === "normal" ? right : list}   // shift content if detail OR list drawer is open
        nav={nav}
        styles={currentStyles}          // memoized - stable reference
      >
        { Views }
      </Content>

      <TopMenu left={left} right={right} nav={nav}/>
      <DualMenu left={left} right={right} nav={nav}/>
      { Navigation }
    </Root>
  );
};
